import { create } from "zustand";
import { propagateMappedParameterChanges } from "../perametric/services/parametricMappingService";

const resolveRows = (nextRows, currentRows) =>
  typeof nextRows === "function" ? nextRows(currentRows) : nextRows;

const getDefinitionValue = (row = {}) =>
  row.defaultValue ?? row.defaultVal ?? row.value ?? "";

const serializeDefinitionValue = (value) =>
  value && typeof value === "object" ? JSON.stringify(value) : String(value);

const getChangedValueIds = (currentRows, nextRows) => {
  const currentById = new Map(currentRows.map((row) => [row.id, row]));

  return nextRows
    .filter((row) => {
      const current = currentById.get(row.id);
      return (
        current &&
        serializeDefinitionValue(getDefinitionValue(current)) !==
          serializeDefinitionValue(getDefinitionValue(row))
      );
    })
    .map((row) => row.id);
};

const propagateChangedValues = (sourceScope, changedSourceIds, state) => {
  if (!changedSourceIds.length) return;

  try {
    propagateMappedParameterChanges({
      sourceScope,
      changedSourceIds,
      globalParameters: state.globalParameters,
      localParameters: state.localParameters,
      parameterMappings: state.parameterMappings,
    });
  } catch (error) {
    console.error("Failed to propagate mapped parameter values:", error);
  }
};

const useParameterStore = create((set, get) => ({
  // PCell parameter values currently being edited on the canvas.
  parameters: [],

  // Design-level parameter definitions. These deliberately live only in
  // memory; a browser refresh creates a fresh Zustand store.
  globalParameters: [],
  localParameters: [],
  parameterMappings: {},

  setParameters: (params) => set({ parameters: params }),

  setParameterSessionState: ({
    globalParameters,
    localParameters,
    parameterMappings,
  }) =>
    set((state) => ({
      globalParameters: globalParameters ?? state.globalParameters,
      localParameters: localParameters ?? state.localParameters,
      parameterMappings: parameterMappings ?? state.parameterMappings,
    })),

  setGlobalParameters: (nextRows) => {
    const currentRows = get().globalParameters;
    const resolvedRows = resolveRows(nextRows, currentRows);
    const changedSourceIds = getChangedValueIds(currentRows, resolvedRows);

    set({ globalParameters: resolvedRows });
    propagateChangedValues("global", changedSourceIds, get());
  },

  setLocalParameters: (nextRows) => {
    const currentRows = get().localParameters;
    const resolvedRows = resolveRows(nextRows, currentRows);
    const changedSourceIds = getChangedValueIds(currentRows, resolvedRows);

    set({ localParameters: resolvedRows });
    propagateChangedValues("local", changedSourceIds, get());
  },

  setParameterMapping: (cellName, parameterName, mapping) =>
    set((state) => {
      if (!cellName || !parameterName) return state;

      const key = `${cellName}:${parameterName}`;
      return {
        parameterMappings: {
          ...state.parameterMappings,
          [key]: mapping,
        },
      };
    }),

  removeParameterMapping: (cellName, parameterName) =>
    set((state) => {
      if (!cellName || !parameterName) return state;

      const key = `${cellName}:${parameterName}`;
      if (!state.parameterMappings[key]) return state;

      const nextMappings = { ...state.parameterMappings };
      delete nextMappings[key];
      return { parameterMappings: nextMappings };
    }),

  removePCellMappings: (cellName) =>
    set((state) => {
      if (!cellName) return state;

      const prefix = `${cellName}:`;
      const nextMappings = Object.fromEntries(
        Object.entries(state.parameterMappings).filter(
          ([key]) => !key.startsWith(prefix)
        )
      );

      return { parameterMappings: nextMappings };
    }),

  updateParameterValue: (name, value) =>
    set((state) => ({
      parameters: state.parameters.map((p) =>
        p.name === name ? { ...p, value } : p
      ),
    })),
}));

export default useParameterStore;
